import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmt, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DecimalInput } from "@/components/DecimalInput";
import { MaterialPicker } from "@/components/MaterialPicker";
import { ScaleWeightDialog } from "@/components/ScaleWeightDialog";
import { CustomerPicker } from "@/components/CustomerPicker";
import { druhShort, druhBadgeClass } from "@/lib/druh";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, Save, FolderPlus, Calculator, Building2, Receipt,
} from "lucide-react";

export default function RecipeEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [meta, setMeta] = useState({});
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSection, setPickerSection] = useState(null);
  const [calcs, setCalcs] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [scaleOpen, setScaleOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/recipes/${id}`).then((r) => {
      setRecipe(r.data);
      setMeta({ name: r.data.name, code: r.data.code || "", author: r.data.author || "", description: r.data.description || "", weight_g: r.data.weight_g ?? null, portions: r.data.portions ?? 1 });
    });
    api.get(`/recipes/${id}/calculations`).then((r) => setCalcs(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!recipe) return null;

  const sections = recipe.sections || [];
  const sectionIds = new Set(sections.map((s) => s.id));
  const itemsIn = (sid) => recipe.items.filter((it) => it.section_id === sid);
  const unassigned = recipe.items.filter((it) => !sectionIds.has(it.section_id));

  const updateItem = (itemId, patch) => {
    setRecipe((r) => ({ ...r, items: r.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) }));
    setDirty(true);
  };
  const removeItem = (itemId) => { setRecipe((r) => ({ ...r, items: r.items.filter((it) => it.id !== itemId) })); setDirty(true); };
  const addMaterials = (mats, sectionId = null) => {
    const existing = new Set(recipe.items.map((i) => i.material_code));
    const newItems = mats.filter((m) => !existing.has(m.code)).map((m) => ({
      id: crypto.randomUUID(), material_code: m.code, material_name: m.name, druh: m.druh,
      category: m.category, unit: m.unit, quantity: 1, section_id: sectionId,
    }));
    setRecipe((r) => ({ ...r, items: [...r.items, ...newItems] }));
    setDirty(true);
    if (newItems.length) toast.success(`Přidáno ${newItems.length} surovin`);
  };
  const openPicker = (sid = null) => { setPickerSection(sid); setPickerOpen(true); };
  const addSection = () => {
    setRecipe((r) => ({ ...r, sections: [...(r.sections || []), { id: crypto.randomUUID(), name: `Sekce ${(r.sections?.length || 0) + 1}` }] }));
    setDirty(true);
  };
  const renameSection = (sid, name) => { setRecipe((r) => ({ ...r, sections: r.sections.map((s) => (s.id === sid ? { ...s, name } : s)) })); setDirty(true); };
  const removeSection = (sid) => {
    setRecipe((r) => ({
      ...r, sections: r.sections.filter((s) => s.id !== sid),
      items: r.items.map((it) => (it.section_id === sid ? { ...it, section_id: null } : it)),
    }));
    setDirty(true);
  };

  const save = async () => {
    const r = await api.put(`/recipes/${id}`, {
      name: meta.name, code: meta.code, author: meta.author, description: meta.description,
      weight_g: meta.weight_g === "" || meta.weight_g == null ? null : Number(meta.weight_g),
      portions: Number(meta.portions) || 1,
      items: recipe.items, sections: recipe.sections || [],
    });
    setRecipe(r.data);
    setDirty(false);
    toast.success("Receptura uložena");
  };

  return (
    <div className="fade-up pb-24">
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="px-10 py-5">
          <button onClick={() => nav("/receptury")} data-testid="recipe-back"
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3">
            <ArrowLeft className="h-4 w-4" /> Zpět na receptury
          </button>
          <div className="flex items-end justify-between gap-6">
            <div className="flex-1">
              <input data-testid="recipe-edit-name" value={meta.name}
                onChange={(e) => { setMeta({ ...meta, name: e.target.value }); setDirty(true); }}
                className="font-display text-3xl font-medium tracking-tight bg-transparent outline-none w-full border-b border-transparent focus:border-border" />
              <div className="text-xs text-muted-foreground mt-2">Receptura (předpis) · {recipe.items.length} surovin · {sections.length} sekcí</div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="recipe-create-calc"
                className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground">
                <Calculator className="h-4 w-4 mr-2" /> Vytvořit kalkulaci
              </Button>
              <Button size="sm" onClick={save} disabled={!dirty} className="rounded-full" data-testid="recipe-save">
                <Save className="h-4 w-4 mr-2" /> Uložit
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-10 py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-border rounded-xl p-5">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Kód</Label>
            <Input data-testid="recipe-edit-code" value={meta.code} className="mt-2"
              onChange={(e) => { setMeta({ ...meta, code: e.target.value }); setDirty(true); }} />
          </div>
          <div className="bg-white border border-border rounded-xl p-5">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Autor</Label>
            <Input data-testid="recipe-edit-author" value={meta.author} className="mt-2"
              onChange={(e) => { setMeta({ ...meta, author: e.target.value }); setDirty(true); }} />
          </div>
          <div className="bg-white border border-border rounded-xl p-5">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Popis</Label>
            <Input data-testid="recipe-edit-desc" value={meta.description} className="mt-2"
              onChange={(e) => { setMeta({ ...meta, description: e.target.value }); setDirty(true); }} />
          </div>
          <div className="bg-white border border-border rounded-xl p-5">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Porce</Label>
              <Button variant="outline" size="sm" className="rounded-full shrink-0 h-7" data-testid="recipe-scale-btn"
                onClick={() => setScaleOpen(true)}>Přepočítat</Button>
            </div>
            <div className="flex items-end gap-2 mt-2">
              <div className="flex-1">
                <span className="text-[11px] text-muted-foreground">Gramáž porce (g)</span>
                <DecimalInput data-testid="recipe-edit-weight" value={meta.weight_g}
                  onValueChange={(n) => { setMeta({ ...meta, weight_g: n }); setDirty(true); }} />
              </div>
              <div className="flex-1">
                <span className="text-[11px] text-muted-foreground">Počet porcí</span>
                <DecimalInput data-testid="recipe-edit-portions" value={meta.portions}
                  onValueChange={(n) => { setMeta({ ...meta, portions: n }); setDirty(true); }} />
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2" data-testid="recipe-total-weight">
              Celkem: <span className="num font-medium text-foreground">{fmt((Number(meta.weight_g) || 0) * (Number(meta.portions) || 0), 0)} g</span>
            </div>
          </div>
        </div>

        <ScaleWeightDialog open={scaleOpen} onOpenChange={setScaleOpen}
          currentWeight={meta.weight_g} currentPortions={meta.portions} sections={sections} items={recipe.items}
          onApply={(newItems, newW, newP) => { setRecipe((c) => ({ ...c, items: newItems })); setMeta((mm) => ({ ...mm, weight_g: newW, portions: newP })); setDirty(true); }} />

        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-medium">Suroviny receptury</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={addSection} data-testid="recipe-add-section" className="rounded-full">
              <FolderPlus className="h-4 w-4 mr-2" /> Přidat sekci
            </Button>
            <Button size="sm" onClick={() => openPicker(null)} data-testid="recipe-add-material" className="rounded-full">
              <Plus className="h-4 w-4 mr-2" /> Přidat surovinu
            </Button>
          </div>
        </div>

        {recipe.items.length === 0 && sections.length === 0 && (
          <div className="bg-white border border-border rounded-xl px-6 py-12 text-center text-muted-foreground text-sm">
            Zatím žádné suroviny. Přidejte sekci nebo surovinu tlačítky výše.
          </div>
        )}

        {sections.map((sec) => (
          <RecipeSection key={sec.id} section={sec} items={itemsIn(sec.id)}
            onRename={renameSection} onRemove={removeSection} onAdd={() => openPicker(sec.id)}
            updateItem={updateItem} removeItem={removeItem} />
        ))}
        {(unassigned.length > 0 || sections.length === 0) && (recipe.items.length > 0 || sections.length === 0) && (
          <RecipeSection section={null} items={unassigned}
            onAdd={() => openPicker(null)} updateItem={updateItem} removeItem={removeItem} />
        )}

        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <Receipt className="h-4 w-4 text-primary" />
            <h3 className="font-display text-lg font-medium">Historie kalkulací ({calcs.length})</h3>
          </div>
          {calcs.length === 0 ? (
            <div className="px-6 py-10 text-center text-muted-foreground text-sm">
              Z této receptury zatím nebyla vytvořena žádná kalkulace.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/60 hover:bg-muted/60">
                  <TableHead>Datum</TableHead>
                  <TableHead>Kdo vytvořil</TableHead>
                  <TableHead>Komu (odběratel)</TableHead>
                  <TableHead className="text-right">Cena/ks</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calcs.map((c) => (
                  <TableRow key={c.id} data-testid={`recipe-calc-${c.id}`} className="cursor-pointer"
                    onClick={() => nav(`/kalkulace/${c.id}`)}>
                    <TableCell className="text-sm">{fmtDate(c.created_at)}</TableCell>
                    <TableCell className="text-sm">{c.author}</TableCell>
                    <TableCell className="text-sm">{c.customer_name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-right num font-semibold">
                      {fmt((c.total || 0) / (c.portions || 1))} Kč/ks
                      <span className="block text-[11px] font-normal text-muted-foreground">dávka {fmt(c.total)} Kč</span>
                    </TableCell>
                    <TableCell className="text-primary text-sm">→</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <MaterialPicker open={pickerOpen} onOpenChange={setPickerOpen}
        onAdd={(mats) => addMaterials(mats, pickerSection)} />
      <CreateCalcDialog open={createOpen} onOpenChange={setCreateOpen} recipe={recipe} recipeId={id}
        dirty={dirty} onSaveFirst={save}
        onCreated={(cid) => { load(); nav(`/kalkulace/${cid}`); }} />
    </div>
  );
}

function RecipeSection({ section, items, onRename, onRemove, onAdd, updateItem, removeItem }) {
  const isSection = !!section;
  return (
    <div className="bg-white border border-border rounded-xl overflow-hidden"
      data-testid={isSection ? `recipe-section-${section.id}` : "recipe-section-none"}>
      <div className="px-6 py-3.5 border-b border-border flex items-center justify-between gap-3 bg-muted/30">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isSection ? (
            <>
              <span className="text-primary text-sm">▸</span>
              <Input value={section.name} data-testid={`recipe-section-name-${section.id}`}
                onChange={(e) => onRename(section.id, e.target.value)}
                className="h-8 font-display font-medium max-w-xs border-transparent hover:border-border focus:border-border bg-transparent" />
            </>
          ) : (<span className="font-display font-medium text-muted-foreground">Bez sekce</span>)}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={onAdd} className="text-primary hover:bg-primary/10"
            data-testid={isSection ? `recipe-section-add-${section.id}` : "recipe-section-add-none"}>
            <Plus className="h-4 w-4 mr-1.5" /> Surovina
          </Button>
          {isSection && (
            <button onClick={() => onRemove(section.id)} data-testid={`recipe-section-remove-${section.id}`}
              title="Odebrat sekci" className="text-muted-foreground hover:text-destructive p-1.5"><Trash2 className="h-4 w-4" /></button>
          )}
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/60 hover:bg-muted/60">
            <TableHead>Surovina</TableHead>
            <TableHead className="w-20">Druh</TableHead>
            <TableHead className="w-16">MJ</TableHead>
            <TableHead className="w-32 text-right">Množství</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 && (
            <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground text-sm">Žádné suroviny.</TableCell></TableRow>
          )}
          {items.map((it) => (
            <TableRow key={it.id} data-testid={`recipe-item-${it.material_code}`}>
              <TableCell>
                <div className="font-medium">{it.material_name}</div>
                <div className="text-xs text-muted-foreground num">{it.material_code} · {it.category}</div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={druhBadgeClass(it.druh)}>
                  {druhShort(it.druh)}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">{it.unit}</TableCell>
              <TableCell className="text-right">
                <DecimalInput data-testid={`recipe-qty-${it.material_code}`} value={it.quantity}
                  className="h-8 text-right num w-24 ml-auto"
                  onValueChange={(n) => updateItem(it.id, { quantity: n })} />
              </TableCell>
              <TableCell>
                <button onClick={() => removeItem(it.id)} data-testid={`recipe-remove-${it.material_code}`}
                  className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CreateCalcDialog({ open, onOpenChange, recipe, recipeId, dirty, onSaveFirst, onCreated }) {
  const [customer, setCustomer] = useState(null);
  const [custPickerOpen, setCustPickerOpen] = useState(false);
  const [author, setAuthor] = useState(recipe.author || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (dirty) await onSaveFirst();
      const r = await api.post(`/recipes/${recipeId}/create-calculation`, {
        customer_id: customer?.id || null,
        author: author || "Neznámý",
      });
      const warns = r.data.warnings?.length || 0;
      if (warns > 0) toast.warning(`Kalkulace vytvořena – ${warns} položek má nulovou cenu (viz upozornění v kalkulaci)`);
      else toast.success("Kalkulace vytvořena");
      onOpenChange(false);
      onCreated(r.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nepodařilo se vytvořit kalkulaci");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Vytvořit kalkulaci z receptury</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-muted-foreground">
              Zkopírují se sekce i suroviny z receptury a ceny se dopočítají dle aktuálního nastavení.
            </p>
            <div>
              <Label>Odběratel</Label>
              <button onClick={() => setCustPickerOpen(true)} data-testid="createcalc-customer-btn"
                className="mt-1.5 w-full flex items-center gap-2 border border-border rounded-lg px-3 py-2.5 text-left hover:bg-muted transition-colors">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                {customer ? (
                  <span className="text-sm">{customer.name}<span className="text-muted-foreground"> · {customer.city || customer.ico}</span></span>
                ) : (<span className="text-sm text-muted-foreground">Vybrat odběratele (volitelné)…</span>)}
              </button>
            </div>
            <div>
              <Label>Vytvořil</Label>
              <Input data-testid="createcalc-author" className="mt-1.5" value={author}
                onChange={(e) => setAuthor(e.target.value)} />
              <p className="text-xs text-muted-foreground mt-2">Marže a koeficienty (pálení, doprava, práce…) nastavíte po vytvoření přímo v kalkulaci.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Zrušit</Button>
            <Button onClick={submit} disabled={busy} data-testid="createcalc-submit">Vytvořit kalkulaci</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CustomerPicker open={custPickerOpen} onOpenChange={setCustPickerOpen} onSelect={setCustomer} />
    </>
  );
}
